import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import { useNotification } from '../../contexts/NotificationContext';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization } from '../../contexts/OrganizationContext';
import EnhancedSidebar from '../../components/ui/EnhancedSidebar';
import Header from '../../components/ui/Header';
import EnhancedBreadcrumb from '../../components/ui/EnhancedBreadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { reviewService } from '../../utils/dataServices';

// Debounce hook for search optimization
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const ReviewManagement = () => {
  const { sidebarExpanded, handleSidebarToggle } = useSidebar();
  const { addNotification } = useNotification();

  // Enhanced state for pagination and performance
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reviews, setReviews] = useState([]);
  const [archivedReviews, setArchivedReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({});
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReview, setSelectedReview] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasMore: false,
    nextCursor: null
  });

  const [archivedPagination, setArchivedPagination] = useState({
    currentPage: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
    hasMore: false
  });

  const [filters, setFilters] = useState({
    status: 'all',
    rating: 'all',
    dateRange: 'all',
    technicianId: 'all'
  });

  // Performance optimization: debounce search
  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const { currentOrg } = useOrganization();

  // Helper function to convert date range to dateFrom/dateTo
  const getDateRangeFilters = (dateRange) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    switch (dateRange) {
      case 'last7days':
        return {
          dateFrom: new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          dateTo: today.toISOString().split('T')[0]
        };
      case 'last30days':
        return {
          dateFrom: new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          dateTo: today.toISOString().split('T')[0]
        };
      case 'last90days':
        return {
          dateFrom: new Date(today.getTime() - 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          dateTo: today.toISOString().split('T')[0]
        };
      case 'thismonth':
        return {
          dateFrom: new Date(now.getFullYear(), now.getMonth(), 1).toISOString().split('T')[0],
          dateTo: today.toISOString().split('T')[0]
        };
      case 'lastmonth':
        const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const lastMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);
        return {
          dateFrom: lastMonth.toISOString().split('T')[0],
          dateTo: lastMonthEnd.toISOString().split('T')[0]
        };
      case 'last3months':
        return {
          dateFrom: new Date(now.getFullYear(), now.getMonth() - 3, 1).toISOString().split('T')[0],
          dateTo: today.toISOString().split('T')[0]
        };
      case 'all':
      default:
        return { dateFrom: '', dateTo: '' };
    }
  };

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    // All users now have access to review management

    loadInitialData();
  }, [user, userProfile, currentOrg]);

  // Load data when filters or search changes
  useEffect(() => {
    if (user && userProfile) {
      loadReviewData(true); // Reset pagination
    }
  }, [filters, debouncedSearchTerm, activeTab, currentOrg]);

  // Listen for automatic archive events
  useEffect(() => {
    const handleReviewsArchived = (event) => {
      const { count, message } = event.detail;
      addNotification(`${count} reviews automatically archived`, 'info');
      // Reload data to reflect changes
      loadReviewData(true);
    };

    window.addEventListener('reviewsArchived', handleReviewsArchived);
    
    return () => {
      window.removeEventListener('reviewsArchived', handleReviewsArchived);
    };
  }, [addNotification]);

  const loadInitialData = async () => {
    setLoading(true);
    try {
      // Load statistics first (cached)
      await loadReviewStatistics();
      // Then load reviews
      await loadReviewData(true);
    } catch (error) {
      console.error('Error loading initial data:', error);
      addNotification('Failed to load review data', 'error');
    } finally {
      setLoading(false);
      }
  };

  const loadReviewStatistics = async () => {
    try {
      const statsResult = await reviewService.getReviewStatisticsCached({}, currentOrg?.id);
      if (statsResult.success) {
        setReviewStats(statsResult.data || {});
      }
    } catch (error) {
      console.error('Error loading review statistics:', error);
    }
  };

  const loadReviewData = async (reset = false) => {
    try {
      if (reset) {
        setLoadingMore(false);
        setPagination(prev => ({ ...prev, currentPage: 1, nextCursor: null }));
        setArchivedPagination(prev => ({ ...prev, currentPage: 1 }));
      } else {
        setLoadingMore(true);
      }

      if (activeTab === 'archived') {
        await loadArchivedReviews(reset);
      } else {
        await loadActiveReviews(reset);
      }
    } catch (error) {
      console.error('Error loading review data:', error);
      addNotification('Failed to load reviews', 'error');
    } finally {
      setLoadingMore(false);
    }
  };

  const loadActiveReviews = async (reset = false) => {
    const currentPage = reset ? 1 : pagination.currentPage;
    
    // Convert date range to dateFrom/dateTo
    const dateFilters = getDateRangeFilters(filters.dateRange);
    const searchFilters = debouncedSearchTerm ? 
      { ...filters, ...dateFilters, organizationId: currentOrg?.id, searchTerm: debouncedSearchTerm, isPublished: activeTab === 'published' ? true : undefined } : 
      { ...filters, ...dateFilters, organizationId: currentOrg?.id, isPublished: activeTab === 'published' ? true : undefined };

    const reviewsResult = await reviewService.getReviewsPaginated(
      searchFilters,
      {
        page: currentPage,
        pageSize: pagination.pageSize,
        cursor: reset ? null : pagination.nextCursor,
        loadDetails: false // Load minimal data for list view
      }
    );

    if (reviewsResult.success) {
      if (reset) {
        setReviews(reviewsResult.data || []);
      } else {
        setReviews(prev => [...prev, ...(reviewsResult.data || [])]);
      }
      
      setPagination({
        currentPage: reviewsResult.pagination?.currentPage || currentPage,
        pageSize: reviewsResult.pagination?.pageSize || pagination.pageSize,
        totalItems: reviewsResult.pagination?.totalItems || 0,
        totalPages: reviewsResult.pagination?.totalPages || 0,
        hasMore: reviewsResult.pagination?.hasMore || false,
        nextCursor: reviewsResult.pagination?.nextCursor
      });
    }
  };

  const loadArchivedReviews = async (reset = false) => {
    const currentPage = reset ? 1 : archivedPagination.currentPage;

    // Convert date range to dateFrom/dateTo
    const dateFilters = getDateRangeFilters(filters.dateRange);
    const archivedResult = await reviewService.getArchivedReviewsPaginated(
      { ...filters, ...dateFilters, organizationId: currentOrg?.id },
      {
        page: currentPage,
        pageSize: archivedPagination.pageSize
      }
    );

    if (archivedResult.success) {
      if (reset) {
        setArchivedReviews(archivedResult.data || []);
      } else {
        setArchivedReviews(prev => [...prev, ...(archivedResult.data || [])]);
      }
      
      setArchivedPagination({
        currentPage: archivedResult.pagination?.currentPage || currentPage,
        pageSize: archivedResult.pagination?.pageSize || archivedPagination.pageSize,
        totalItems: archivedResult.pagination?.totalItems || 0,
        totalPages: archivedResult.pagination?.totalPages || 0,
        hasMore: archivedResult.pagination?.hasMore || false
      });
    }
  };

  const loadMoreReviews = () => {
    if (activeTab === 'archived') {
      if (archivedPagination.hasMore && !loadingMore) {
        setArchivedPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
        loadArchivedReviews(false);
      }
    } else {
      if (pagination.hasMore && !loadingMore) {
        setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
        loadActiveReviews(false);
      }
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleTabChange = (newTab) => {
    setActiveTab(newTab);
    // Clear search when switching tabs
    setSearchTerm('');
  };

  const handleViewReview = async (review) => {
    try {
      // Load full review details
      const detailResult = await reviewService.getReviewDetails(review.id);
      if (detailResult.success) {
        setSelectedReview(detailResult.data);
        setShowDetailModal(true);
      } else {
        addNotification('Failed to load review details', 'error');
      }
    } catch (error) {
      console.error('Error loading review details:', error);
      addNotification('Failed to load review details', 'error');
    }
  };

  const handlePublish = async (reviewId) => {
    const result = await reviewService.publishReview(reviewId);
    if (result.success) {
      addNotification('Review published', 'success');
      await loadReviewStatistics();
      await loadReviewData(true);
    } else {
      addNotification(result.error || 'Failed to publish review', 'error');
    }
  };

  const handleUnpublish = async (reviewId) => {
    const result = await reviewService.unpublishReview(reviewId);
    if (result.success) {
      addNotification('Review unpublished', 'success');
      await loadReviewStatistics();
      await loadReviewData(true);
    } else {
      addNotification(result.error || 'Failed to unpublish review', 'error');
    }
  };

  const handleDeleteReview = async (reviewId) => {
    console.log('Delete button clicked for review:', reviewId);
    if (window.confirm('Are you sure you want to delete this review?')) {
      console.log('Delete confirmed, calling service...');
      const result = await reviewService.deleteReview(reviewId);
      console.log('Delete result:', result);
      if (result.success) {
        // Clear all caches first
        reviewService.clearCache('review_stats');
        reviewService.clearCache();
        
        // Force reload all data
        await loadReviewStatistics();
        await loadReviewData(true);
        
        addNotification('Review deleted successfully', 'success');
      } else {
        addNotification(`Failed to delete review: ${result.error}`, 'error');
      }
    }
  };

  const handleArchiveReview = async (reviewId, reason = 'Manually archived') => {
    console.log('Archive button clicked for review:', reviewId);
    if (window.confirm('Are you sure you want to archive this review? It will be moved to the archived tab.')) {
      console.log('Archive confirmed, calling service...');
      const result = await reviewService.archiveReview(reviewId, reason);
      console.log('Archive result:', result);
      if (result.success) {
        // Clear all caches first
        reviewService.clearCache('review_stats');
        reviewService.clearCache();
        
        // Force reload all data
        await loadReviewStatistics();
        await loadReviewData(true);
        
        addNotification('Review archived successfully', 'success');
      } else {
        addNotification(`Failed to archive review: ${result.error}`, 'error');
      }
    }
  };

  const handleRestoreReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to restore this review? It will be moved back to active reviews.')) {
      const result = await reviewService.restoreReview(reviewId);
      if (result.success) {
        // Clear all caches first
        reviewService.clearCache('review_stats');
        reviewService.clearCache();
        
        // Force reload all data
        await loadReviewStatistics();
        await loadReviewData(true);
        
        addNotification('Review restored successfully', 'success');
      } else {
        addNotification(`Failed to restore review: ${result.error}`, 'error');
      }
    }
  };

  const handleApproveReview = async (reviewId) => {
    if (window.confirm('Are you sure you want to approve this review? It will be marked as submitted and visible publicly.')) {
      const result = await reviewService.approveReview(reviewId);
      if (result.success) {
        // Clear all caches first
        reviewService.clearCache('review_stats');
        reviewService.clearCache();
        
        // Force reload all data
        await loadReviewStatistics();
        await loadReviewData(true);
        
        addNotification('Review approved successfully', 'success');
      } else {
        addNotification(`Failed to approve review: ${result.error}`, 'error');
      }
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, index) => (
      <span
        key={index}
        className={`text-lg ${
          index < rating ? 'text-yellow-400' : 'text-gray-300'
        }`}
      >
        ★
      </span>
    ));
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-warning-50 text-warning', label: 'Pending' },
      submitted: { color: 'bg-success-50 text-success', label: 'Submitted' },
      responded: { color: 'bg-primary-50 text-primary', label: 'Responded' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-success';
    if (rating >= 3) return 'text-warning';
    return 'text-error';
  };

  // Memoized filtered reviews for performance
  const filteredReviews = useMemo(() => {
    const displayReviews = activeTab === 'archived' ? archivedReviews : reviews;
    
    return displayReviews.filter(review => {
      const matchesTab = activeTab === 'all' || activeTab === 'archived' ||
      (activeTab === 'positive' && review.rating >= 4) ||
      (activeTab === 'negative' && review.rating <= 2) ||
      (activeTab === 'pending' && review.status === 'pending');
    
      return matchesTab;
    });
  }, [reviews, archivedReviews, activeTab]);

  // Memoized tab counts for performance
  const tabCounts = useMemo(() => {
    const activeReviews = reviews;
    return {
      all: activeReviews.length,
      published: activeReviews.filter(r => r.is_published).length,
      positive: activeReviews.filter(r => r.rating >= 4).length,
      negative: activeReviews.filter(r => r.rating <= 2).length,
      pending: activeReviews.filter(r => r.status === 'pending').length,
      archived: archivedReviews.length
    };
  }, [reviews, archivedReviews]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header 
        onSidebarToggle={handleSidebarToggle}
        sidebarExpanded={sidebarExpanded}
      />
        <EnhancedSidebar 
        expanded={sidebarExpanded}
        onToggle={handleSidebarToggle}
      />
        
        <main className={`pt-16 transition-all duration-300 ease-in-out ${
        sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
      }`}>
          <div className="p-6">
            <div className="max-w-7xl mx-auto">
              <div className="mb-8">
                <div className="h-8 bg-card-hover rounded animate-pulse mb-2"></div>
                <div className="h-4 bg-card-hover rounded w-1/3 animate-pulse"></div>
              </div>
              
              {/* Stats skeleton */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="bg-card rounded-lg border border-border p-6">
                    <div className="h-4 bg-card-hover rounded mb-3 animate-pulse"></div>
                    <div className="h-8 bg-card-hover rounded animate-pulse"></div>
                  </div>
                ))}
              </div>
              
              {/* Reviews list skeleton */}
              <div className="bg-card rounded-lg border border-border">
                <div className="divide-y divide-border">
                  {[...Array(5)].map((_, i) => (
                    <div key={i} className="p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-10 h-10 bg-card-hover rounded-full animate-pulse"></div>
                        <div className="flex-1">
                          <div className="h-4 bg-card-hover rounded mb-2 animate-pulse"></div>
                          <div className="h-3 bg-card-hover rounded w-2/3 mb-2 animate-pulse"></div>
                          <div className="h-3 bg-card-hover rounded w-1/2 animate-pulse"></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Review Management - FieldFlow Pro</title>
        <meta name="description" content="Manage customer reviews and feedback" />
      </Helmet>

      <Header 
        onSidebarToggle={handleSidebarToggle}
        sidebarExpanded={sidebarExpanded}
      />
      <EnhancedSidebar 
        expanded={sidebarExpanded}
        onToggle={handleSidebarToggle}
      />

                <main className={`pt-16 transition-all duration-300 ease-in-out ${
        sidebarExpanded ? 'lg:pl-64' : 'lg:pl-16'
      }`}>
        <EnhancedBreadcrumb />

        <div className="max-w-full mx-auto p-6">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-foreground mb-2">
                  Review Management
                </h1>
                <p className="text-muted-foreground">
                  Monitor and manage customer feedback and reviews
                </p>
              </div>
            </div>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Reviews</p>
                  <p className="text-2xl font-bold text-foreground">
                    {reviewStats.total_reviews || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-primary-50 rounded-lg flex items-center justify-center">
                  <Icon name="Star" size={24} className="text-primary" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Average Rating</p>
                  <p className="text-2xl font-bold text-foreground">
                    {reviewStats.average_rating || '0.0'}
                  </p>
                </div>
                <div className="w-12 h-12 bg-success-50 rounded-lg flex items-center justify-center">
                  <Icon name="TrendingUp" size={24} className="text-success" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Response Rate</p>
                  <p className="text-2xl font-bold text-foreground">
                    {reviewStats.response_rate || 0}%
                  </p>
                </div>
                <div className="w-12 h-12 bg-warning-50 rounded-lg flex items-center justify-center">
                  <Icon name="MessageCircle" size={24} className="text-warning" />
                </div>
              </div>
            </div>

            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Positive Reviews</p>
                  <p className="text-2xl font-bold text-foreground">
                    {reviewStats.positive_reviews || 0}
                  </p>
                </div>
                <div className="w-12 h-12 bg-success-50 rounded-lg flex items-center justify-center">
                  <Icon name="ThumbsUp" size={24} className="text-success" />
                </div>
              </div>
            </div>
          </div>

          {/* Filters and Search */}
          <div className="bg-card rounded-lg border border-border p-6 mb-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Search Reviews
                </label>
                <Input
                  type="text"
                  placeholder="Search by customer, comment..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  iconName="Search"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Status
                </label>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="submitted">Submitted</option>
                  <option value="responded">Responded</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Rating
                </label>
                <select
                  value={filters.rating}
                  onChange={(e) => handleFilterChange('rating', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">All Ratings</option>
                  <option value="5">5 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="2">2 Stars</option>
                  <option value="1">1 Star</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  Date Range
                </label>
                <select
                  value={filters.dateRange}
                  onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                  className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="all">✓ All Time</option>
                  <option value="last7days">Last 7 Days</option>
                  <option value="last30days">Last 30 Days</option>
                  <option value="last90days">Last 90 Days</option>
                  <option value="thismonth">This Month</option>
                  <option value="lastmonth">Last Month</option>
                  <option value="last3months">Last 3 Months</option>
                </select>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row gap-4 mb-6">
            <div className="flex-1">
              <Input
                placeholder="Search reviews by name, email, or comment..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                iconName="Search"
                className="w-full"
              />
            </div>
            <div className="flex gap-2">
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className="px-3 py-2 border border-border rounded-lg bg-card text-foreground"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="submitted">Submitted</option>
                <option value="responded">Responded</option>
              </select>
              <select
                value={filters.rating}
                onChange={(e) => handleFilterChange('rating', e.target.value)}
                className="px-3 py-2 border border-border rounded-lg bg-card text-foreground"
              >
                <option value="all">All Ratings</option>
                <option value="5">5 Stars</option>
                <option value="4">4 Stars</option>
                <option value="3">3 Stars</option>
                <option value="2">2 Stars</option>
                <option value="1">1 Star</option>
              </select>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mb-6">
            {[
              { id: 'all', label: 'All Reviews', count: tabCounts.all },
              { id: 'published', label: 'Published', count: tabCounts.published },
              { id: 'positive', label: 'Positive', count: tabCounts.positive },
              { id: 'negative', label: 'Negative', count: tabCounts.negative },
              { id: 'pending', label: 'Pending', count: tabCounts.pending },
              { id: 'archived', label: 'Archived', count: tabCounts.archived, icon: 'Archive' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-micro flex items-center space-x-1 ${
                  activeTab === tab.id
                    ? 'bg-primary text-white'
                    : 'text-muted-foreground hover:bg-card-hover'
                }`}
              >
                {tab.icon && <Icon name={tab.icon} size={14} />}
                <span>{tab.label} ({tab.count})</span>
              </button>
            ))}
          </div>

          {/* Reviews List */}
          <div className="bg-card rounded-lg border border-border">
            {/* Pagination Info */}
            {activeTab !== 'archived' && pagination.totalItems > 0 && (
              <div className="px-6 py-3 border-b border-border bg-card-hover/50">
                <div className="flex justify-between items-center text-sm text-text-muted">
                  <span>
                    Showing {filteredReviews.length} of {pagination.totalItems} reviews
                    {debouncedSearchTerm && ` for "${debouncedSearchTerm}"`}
                  </span>
                  {pagination.hasMore && (
                    <span>More results available</span>
                  )}
                </div>
              </div>
            )}
            
            {activeTab === 'archived' && archivedPagination.totalItems > 0 && (
              <div className="px-6 py-3 border-b border-border bg-card-hover/50">
                <div className="flex justify-between items-center text-sm text-text-muted">
                  <span>
                    Showing {filteredReviews.length} of {archivedPagination.totalItems} archived reviews
                  </span>
                  {archivedPagination.hasMore && (
                    <span>More results available</span>
                  )}
                </div>
              </div>
            )}

            {filteredReviews.length === 0 ? (
              <div className="text-center py-12">
                <Icon name="Star" size={48} className="mx-auto mb-4 text-text-muted opacity-50" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {loading ? 'Loading reviews...' : 'No reviews found'}
                </h3>
                <p className="text-muted-foreground">
                  {loading ? 'Please wait while we load your reviews' :
                   debouncedSearchTerm ? `No reviews match "${debouncedSearchTerm}"` :
                   searchTerm || filters.status !== 'all' || filters.rating !== 'all'
                    ? 'Try adjusting your filters to see more reviews.'
                    : 'Customer reviews will appear here as they are submitted.'}
                </p>
              </div>
            ) : (
              <>
              <div className="divide-y divide-border">
                {filteredReviews.map((review) => (
                  <div key={review.id} className="p-6 hover:bg-card-hover transition-micro">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-3">
                          <div className="w-10 h-10 bg-primary-50 rounded-full flex items-center justify-center">
                            <Icon name="User" size={20} className="text-primary" />
                          </div>
                          
                          <div>
                            <h3 className="font-medium text-foreground">
                              {review.reviewer_name || 'Anonymous'}
                            </h3>
                            <div className="flex items-center space-x-2">
                              <div className="flex">{renderStars(review.rating)}</div>
                              <span className={`font-medium ${getRatingColor(review.rating)}`}>
                                {review.rating}/5
                              </span>
                              <span className="text-text-muted">•</span>
                              <span className="text-sm text-text-muted">
                                {review.is_archived 
                                  ? `Archived ${new Date(review.archived_at).toLocaleDateString()}`
                                  : new Date(review.submitted_at || review.created_at).toLocaleDateString()
                                }
                              </span>
                              {review.is_archived && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                  <Icon name="Archive" size={10} className="mr-1" />
                                  Archived
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {review.comment && (
                          <p className="text-muted-foreground mb-4 pl-14">
                            "{review.comment}"
                          </p>
                        )}

                        <div className="flex items-center space-x-6 text-sm text-text-muted pl-14">
                          <span className="flex items-center space-x-1">
                            <Icon name="Briefcase" size={14} />
                            <span>{review.appointment?.service?.name || 'Service'}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="User" size={14} />
                            <span>{review.appointment?.technician?.full_name || 'Technician'}</span>
                          </span>
                          <span className="flex items-center space-x-1">
                            <Icon name="MapPin" size={14} />
                            <span>{review.appointment?.property?.address || 'Location'}</span>
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2 z-10">
                        {getStatusBadge(review.status)}
                        
                        {/* Show approve/publish for pending */}
                        {review.status === 'pending' && (
                        <Button
                          variant="ghost"
                          size="sm"
                            iconName="Check"
                          iconSize={16}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleApproveReview(review.id);
                            }}
                            className="text-text-muted hover:text-success z-10"
                            title="Approve review"
                          />
                        )}

                        {/* Publish/Unpublish toggles for submitted */}
                        {review.status === 'submitted' && (
                          review.is_published ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="EyeOff"
                              iconSize={16}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleUnpublish(review.id);
                              }}
                              className="text-text-muted hover:text-warning z-10"
                              title="Unpublish review"
                            />
                          ) : (
                            <Button
                              variant="ghost"
                              size="sm"
                              iconName="Eye"
                              iconSize={16}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handlePublish(review.id);
                              }}
                              className="text-text-muted hover:text-success z-10"
                              title="Publish review"
                            />
                          )
                        )}
                        
                        {activeTab === 'archived' ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            iconName="RotateCcw"
                            iconSize={16}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleRestoreReview(review.id);
                            }}
                            className="text-text-muted hover:text-success z-10"
                            title="Restore review"
                        />
                        ) : (
                        <Button
                          variant="ghost"
                          size="sm"
                            iconName="Archive"
                          iconSize={16}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleArchiveReview(review.id);
                            }}
                            className="text-text-muted hover:text-warning z-10"
                            title="Archive review"
                        />
                        )}
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="Trash2"
                          iconSize={16}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleDeleteReview(review.id);
                          }}
                          className="text-text-muted hover:text-error z-10"
                          title="Delete review"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                
                {/* Load More Button */}
                {((activeTab === 'archived' && archivedPagination.hasMore) || 
                  (activeTab !== 'archived' && pagination.hasMore)) && (
                  <div className="p-6 border-t border-border bg-card-hover/50">
                    <Button
                      variant="secondary"
                      onClick={loadMoreReviews}
                      disabled={loadingMore}
                      className="w-full"
                      iconName={loadingMore ? "Loader" : "ChevronDown"}
                      iconSize={16}
                    >
                      {loadingMore ? 'Loading...' : 'Load More Reviews'}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </main>

      {/* Review Detail Modal */}
      {showDetailModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-300 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-foreground">Review Details</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  iconName="X"
                  iconSize={20}
                  onClick={() => setShowDetailModal(false)}
                />
              </div>

              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
                    <Icon name="User" size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-foreground">
                      {selectedReview.reviewer_name || 'Anonymous'}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <div className="flex">{renderStars(selectedReview.rating)}</div>
                      <span className={`font-medium ${getRatingColor(selectedReview.rating)}`}>
                        {selectedReview.rating}/5
                      </span>
                    </div>
                  </div>
                </div>

                {selectedReview.comment && (
                  <div>
                    <h4 className="font-medium text-foreground mb-2">Comment</h4>
                    <p className="text-muted-foreground bg-card-hover p-4 rounded-lg">
                      "{selectedReview.comment}"
                    </p>
                  </div>
                )}

                {/* Customer Information */}
                {selectedReview.appointment?.contact && (
                  <div className="bg-card-hover/50 rounded-lg p-4">
                    <h4 className="font-medium text-foreground mb-3 flex items-center">
                      <Icon name="User" size={16} className="mr-2" />
                      Customer Information
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                      <div className="space-y-2">
                        <p><strong>Name:</strong> {selectedReview.appointment.contact.name}</p>
                        <p><strong>Email:</strong> {selectedReview.appointment.contact.email || 'Not provided'}</p>
                        <p><strong>Phone:</strong> {selectedReview.appointment.contact.phone || 'Not provided'}</p>
                        {selectedReview.appointment.contact.organization_name && (
                          <p><strong>Company:</strong> {selectedReview.appointment.contact.organization_name}</p>
                        )}
                      </div>
                      <div className="space-y-2">
                        {selectedReview.appointment.contact.customer_type && (
                          <p><strong>Type:</strong> {selectedReview.appointment.contact.customer_type}</p>
                        )}
                        {selectedReview.appointment.contact.lead_sources?.name && (
                          <p><strong>Source:</strong> {selectedReview.appointment.contact.lead_sources.name}</p>
                        )}
                        {selectedReview.appointment.contact.pipeline_stage && (
                          <p><strong>Stage:</strong> {selectedReview.appointment.contact.pipeline_stage}</p>
                        )}
                        {selectedReview.appointment.contact.industry && (
                          <p><strong>Industry:</strong> {selectedReview.appointment.contact.industry}</p>
                        )}
                      </div>
                    </div>
                    
                    {selectedReview.appointment.contact.tags && selectedReview.appointment.contact.tags.length > 0 && (
                      <div className="mt-3">
                        <p className="text-sm font-medium mb-2">Tags:</p>
                        <div className="flex flex-wrap gap-1">
                          {selectedReview.appointment.contact.tags.map((tag, index) => (
                            <span key={index} className="px-2 py-1 bg-primary-50 text-primary text-xs rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="Briefcase" size={16} className="mr-2" />
                      Service Details
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Service:</strong> {selectedReview.appointment?.service?.name || 'N/A'}</p>
                      <p><strong>Category:</strong> {selectedReview.appointment?.service?.category || 'N/A'}</p>
                      <p><strong>Date:</strong> {selectedReview.appointment?.scheduled_date ? new Date(selectedReview.appointment.scheduled_date).toLocaleDateString() : 'N/A'}</p>
                      <p><strong>Status:</strong> {selectedReview.appointment?.status || 'N/A'}</p>
                      {selectedReview.appointment?.estimated_duration && (
                        <p><strong>Duration:</strong> {selectedReview.appointment.estimated_duration} minutes</p>
                      )}
                      {selectedReview.appointment?.service?.base_price && (
                        <p><strong>Base Price:</strong> ${selectedReview.appointment.service.base_price}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <h4 className="font-medium text-foreground mb-2 flex items-center">
                      <Icon name="MapPin" size={16} className="mr-2" />
                      Location & Technician
                    </h4>
                    <div className="space-y-2 text-sm">
                      <p><strong>Address:</strong> {selectedReview.appointment?.property?.address || 'N/A'}</p>
                      <p><strong>City:</strong> {selectedReview.appointment?.property?.city || 'N/A'}</p>
                      <p><strong>State:</strong> {selectedReview.appointment?.property?.state || 'N/A'}</p>
                      {selectedReview.appointment?.property?.property_type && (
                        <p><strong>Property Type:</strong> {selectedReview.appointment.property.property_type}</p>
                      )}
                      <div className="pt-2 border-t border-border">
                        <p><strong>Technician:</strong> {selectedReview.appointment?.technician ? 
                          `${selectedReview.appointment.technician.first_name} ${selectedReview.appointment.technician.last_name}` 
                          : 'N/A'}</p>
                        {selectedReview.appointment?.technician?.email && (
                          <p><strong>Technician Email:</strong> {selectedReview.appointment.technician.email}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-card-hover/50 rounded-lg p-4">
                  <h4 className="font-medium text-foreground mb-2 flex items-center">
                    <Icon name="Star" size={16} className="mr-2" />
                    Review Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div className="space-y-2">
                      <p><strong>Status:</strong> {getStatusBadge(selectedReview.status)}</p>
                      <p><strong>Submitted:</strong> {new Date(selectedReview.submitted_at || selectedReview.created_at).toLocaleString()}</p>
                      <p><strong>Reviewer Email:</strong> {selectedReview.reviewer_email || 'Not provided'}</p>
                    </div>
                    <div className="space-y-2">
                      <p><strong>Reviewer Phone:</strong> {selectedReview.reviewer_phone || 'Not provided'}</p>
                      {selectedReview.is_archived && (
                        <>
                          <p><strong>Archived:</strong> {new Date(selectedReview.archived_at).toLocaleString()}</p>
                          <p><strong>Archive Reason:</strong> {selectedReview.archived_reason || 'No reason provided'}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-8 flex justify-end space-x-3">
                <Button variant="ghost" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
                <Button variant="primary" iconName="MessageCircle" iconSize={16}>
                  Respond to Review
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReviewManagement;